import { useState } from 'react'
import {
  Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Space, Switch, Table, Tag, Typography,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import {
  useRdoTemplates, useCreateRdoTemplate, useUpdateRdoTemplate, useDeleteRdoTemplate,
  type RdoTemplate, type RdoTemplateItem,
} from '@/hooks/useRdoTemplates'

const { Title } = Typography

interface ItemRow {
  key: string
  descrizione: string
  quantita: number
  ordine: number
}

const emptyItem = (): ItemRow => ({
  key: String(Date.now() + Math.random()),
  descrizione: '',
  quantita: 1,
  ordine: 0,
})

export default function RdoTemplatePage() {
  const { data: templates = [], isLoading } = useRdoTemplates()
  const createMutation = useCreateRdoTemplate()
  const updateMutation = useUpdateRdoTemplate()
  const deleteMutation = useDeleteRdoTemplate()

  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [items, setItems] = useState<ItemRow[]>([emptyItem()])
  const [form] = Form.useForm()

  const filtered = templates.filter(t =>
    t.parola_breve.toLowerCase().includes(search.toLowerCase()) ||
    t.nome.toLowerCase().includes(search.toLowerCase())
  )

  const openCreate = () => {
    setEditingId(null)
    setItems([emptyItem()])
    form.resetFields()
    form.setFieldsValue({ attivo: true })
    setModalOpen(true)
  }

  const openEdit = (t: RdoTemplate) => {
    setEditingId(t.id)
    const rows: ItemRow[] = t.items.map((it, idx) => ({
      key: String(it.id ?? idx),
      descrizione: it.descrizione,
      quantita: Number(it.quantita),
      ordine: it.ordine,
    }))
    setItems(rows.length ? rows : [emptyItem()])
    form.setFieldsValue({ nome: t.nome, parola_breve: t.parola_breve, attivo: t.attivo })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    form.resetFields()
    setItems([emptyItem()])
    setEditingId(null)
  }

  const addItem = () => setItems(prev => [...prev, emptyItem()])

  const updateItem = (key: string, field: keyof ItemRow, value: any) => {
    setItems(prev => prev.map(r => r.key === key ? { ...r, [field]: value } : r))
  }

  const removeItem = (key: string) => {
    setItems(prev => prev.filter(r => r.key !== key))
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const parola_breve = values.parola_breve.trim().toUpperCase()
      const itemsPayload = items
        .filter(r => r.descrizione.trim())
        .map((r, idx) => ({ descrizione: r.descrizione.trim(), quantita: r.quantita, ordine: idx }))

      if (itemsPayload.length === 0) {
        form.setFields([{ name: 'parola_breve', errors: ['Aggiungere almeno un articolo'] }])
        return
      }

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data: { ...values, parola_breve, items: itemsPayload } })
      } else {
        await createMutation.mutateAsync({ ...values, parola_breve, items: itemsPayload })
      }
      closeModal()
    } catch (_) {}
  }

  const itemColumns: ColumnsType<ItemRow> = [
    {
      title: 'Descrizione articolo',
      dataIndex: 'descrizione',
      render: (v, row) => (
        <Input
          value={v}
          onChange={e => updateItem(row.key, 'descrizione', e.target.value)}
          placeholder="Es. FILTRO ARIA"
        />
      ),
    },
    {
      title: 'Qtà',
      dataIndex: 'quantita',
      width: 90,
      render: (v, row) => (
        <InputNumber
          value={v}
          min={0.01}
          step={1}
          style={{ width: '100%' }}
          onChange={val => updateItem(row.key, 'quantita', val ?? 1)}
        />
      ),
    },
    {
      title: '',
      width: 40,
      render: (_, row) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(row.key)}
          disabled={items.length === 1}
        />
      ),
    },
  ]

  const tableColumns: ColumnsType<RdoTemplate> = [
    {
      title: 'Parola breve',
      dataIndex: 'parola_breve',
      render: v => <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 13 }}>{v}</Tag>,
    },
    {
      title: 'Nome display',
      dataIndex: 'nome',
    },
    {
      title: 'Articoli',
      dataIndex: 'items',
      width: 80,
      align: 'center',
      render: (items: RdoTemplateItem[]) => <Tag>{items.length}</Tag>,
    },
    {
      title: 'Attivo',
      dataIndex: 'attivo',
      width: 80,
      align: 'center',
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Sì' : 'No'}</Tag>,
    },
    {
      title: 'Azioni',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Eliminare il template?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Elimina"
            cancelText="Annulla"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Template RDO</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nuovo template
        </Button>
      </div>

      <Card>
        <Input.Search
          placeholder="Cerca parola breve o nome..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 16, maxWidth: 360 }}
          allowClear
        />
        <Table
          columns={tableColumns}
          dataSource={filtered}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: 'Nessun template configurato' }}
        />
      </Card>

      <Modal
        title={editingId ? 'Modifica template' : 'Nuovo template RDO'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={closeModal}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="nome"
            label="Nome display"
            rules={[{ required: true, message: 'Campo obbligatorio' }]}
          >
            <Input placeholder="Es. Tagliando base" />
          </Form.Item>

          <Form.Item
            name="parola_breve"
            label="Parola breve (trigger)"
            rules={[{ required: true, message: 'Campo obbligatorio' }]}
            extra="Il testo esatto che l'utente deve digitare nel titolo RDO per attivare il template"
          >
            <Input
              placeholder="Es. TAGLIANDO BASE"
              onBlur={e => form.setFieldValue('parola_breve', e.target.value.trim().toUpperCase())}
              style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
            />
          </Form.Item>

          <Form.Item name="attivo" label="Attivo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>

        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Typography.Text strong>Articoli del template</Typography.Text>
            <Button size="small" icon={<PlusOutlined />} onClick={addItem}>
              Aggiungi articolo
            </Button>
          </div>
          <Table
            columns={itemColumns}
            dataSource={items}
            rowKey="key"
            size="small"
            pagination={false}
            locale={{ emptyText: 'Nessun articolo — aggiungine almeno uno' }}
          />
        </div>
      </Modal>
    </div>
  )
}
